import { atom } from "jotai";

import { addressBookApi } from "../services";
import type { Category } from "../types";
import { searchQueryAtom } from "./contact";
import { paneAtom } from "./pane";
import { addressBookAtom } from "./state";

export const categoriesAtom = atom((get) => get(addressBookAtom).categories);

// Derived atom for selected category
export const selectedCategoryAtom = atom(
  (get) => get(addressBookAtom).selectedCategory,
);

// Loading state for categories list
export const categoriesLoadingAtom = atom(false);

// Key of the category whose contacts are currently being fetched
export const loadingCategoryKeyAtom = atom<string | null>(null);

export const loadCategoriesAtom = atom(null, async (get, set) => {
  // Prevent concurrent requests
  const isLoading = get(categoriesLoadingAtom);
  if (isLoading) {
    return;
  }

  // Check if already loaded
  const currentCategories = get(categoriesAtom);
  if (currentCategories.length > 0) {
    return;
  }

  try {
    // Set loading state before starting the fetch
    set(categoriesLoadingAtom, true);

    const addressBookName = get(addressBookAtom).addressBookName;

    const categories = await addressBookApi.fetchCategories(addressBookName);

    set(addressBookAtom, (draft) => {
      draft.categories = categories;
    });
  } catch (e) {
    console.error("Failed to load categories:", e);
  } finally {
    set(categoriesLoadingAtom, false);
  }
});

export const selectCategoryAtom = atom(
  null,
  async (get, set, category: Category) => {
    // Reset search when switching categories
    set(searchQueryAtom, "");

    // On narrow layouts, a new category always lands on the list
    set(paneAtom, "list");

    // Update selected category
    set(addressBookAtom, (draft) => {
      draft.selectedCategory = category;
      draft.selectedContact = null;
    });

    try {
      // Set loading category key to show spinner in the UI
      set(loadingCategoryKeyAtom, category.key);

      const contacts = await addressBookApi.fetchContacts(
        get(addressBookAtom).addressBookName,
        category.type,
        category.key,
      );

      set(addressBookAtom, (draft) => {
        draft.contacts = contacts;
      });
    } catch (e) {
      console.error("Failed to load contacts:", e);
    } finally {
      set(loadingCategoryKeyAtom, null);
    }
  },
);
